import { combineReducers } from 'redux';

import { createFlightBookingPanelEntitiesReducer } from './entities';
import * as fromEntities from './entities';
import { createFlightBookingPanelFeaturesReducer } from './features';
import * as fromFeatures from './features';

import type { GetExitingItineraryId } from './entities/flights';
import type { AttributeType } from '@web-platform/shared-universal-constants/src/filterAttributes';
import type { AgentBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/agentsBookingPanel';
import type { CarrierBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/carriersBookingPanel';
import type {
  ErrorType,
  FilterStatusMapBookingPanel,
  ItineraryBookingPanel,
  ItineraryByIdBookingPanel,
} from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/itinerariesBookingPanel';
import type { LegBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/legsBookingPanel';
import type { SegmentBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/segmentsBookingPanel';
import type { PartnerMetadataTrustLevelBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/partnerMetadataBookingPanel';
import type { PaymentDiscountsBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/paymentDiscountsBookingPanel';
import type { PlaceBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/placesBookingPanel';
import type { FlightBookingPanelState } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/flightBookingPanel';
import type { TransitionedToFlightBookingPanelState } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/transitionedToFlightBookingPanel';
import type { EntityId } from '@web-platform/shared-universal-types-utils/src/utils';

export type { FlightBookingPanelState } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/flightBookingPanel';
export type { GetExitingItineraryId } from './entities/flights';

export const createFlightBookingPanelReducer = (
  isTokenPanel: () => boolean,
  getExitingId: GetExitingItineraryId,
) =>
  combineReducers({
    entities: createFlightBookingPanelEntitiesReducer(isTokenPanel, getExitingId),
    features: createFlightBookingPanelFeaturesReducer(),
  });

// legs
export const flightLegBookingPanel = (
  state: FlightBookingPanelState,
  id: string,
): LegBookingPanel => fromEntities.flightLegBookingPanel(state.entities, id);
export const flightAllLegsBookingPanel = (
  state: FlightBookingPanelState,
): Record<string, LegBookingPanel> => fromEntities.flightAllLegsBookingPanel(state.entities);

// carriers
export const flightCarrierBookingPanel = (
  state: FlightBookingPanelState,
  id: number,
): CarrierBookingPanel => fromEntities.flightCarrierBookingPanel(state.entities, id);
export const flightAllCarriersBookingPanel = (
  state: FlightBookingPanelState,
): Record<number, CarrierBookingPanel> =>
  fromEntities.flightAllCarriersBookingPanel(state.entities);

// segments
export const flightSegmentBookingPanel = (
  state: FlightBookingPanelState,
  id: string,
): SegmentBookingPanel => fromEntities.flightSegmentBookingPanel(state.entities, id);
export const flightAllSegmentsBookingPanel = (
  state: FlightBookingPanelState,
): Record<string, SegmentBookingPanel> =>
  fromEntities.flightAllSegmentsBookingPanel(state.entities);

// places
export const allPlacesByEntityIdBookingPanel = (
  state: FlightBookingPanelState,
): Record<EntityId, PlaceBookingPanel> =>
  fromEntities.allPlacesByEntityIdBookingPanel(state.entities);

// agents
export const agentBookingPanel = (state: FlightBookingPanelState, id: string): AgentBookingPanel =>
  fromEntities.agentBookingPanel(state.entities, id);
export const allAgentsBookingPanel = (
  state: FlightBookingPanelState,
): Record<string, AgentBookingPanel> => fromEntities.allAgentsBookingPanel(state.entities);

// partnerMetadata
export const getPartnerMetadataBookingPanel = (
  state: FlightBookingPanelState,
  id: string,
): PartnerMetadataTrustLevelBookingPanel =>
  fromEntities.getPartnerMetadataBookingPanel(state.entities, id);
export const getAllPartnerMetadataBookingPanel = (
  state: FlightBookingPanelState,
): Record<string, PartnerMetadataTrustLevelBookingPanel> =>
  fromEntities.getAllPartnerMetadataBookingPanel(state.entities);

// itineraries
export const flightItineraryBookingPanel = (
  state: FlightBookingPanelState,
  id: string,
): ItineraryBookingPanel => fromEntities.flightItineraryBookingPanel(state.entities, id);
export const flightAllItinerariesBookingPanel = (
  state: FlightBookingPanelState,
): ItineraryByIdBookingPanel => fromEntities.flightAllItinerariesBookingPanel(state.entities);
export const getItineraryFilterStatusMapBookingPanel = (
  state: FlightBookingPanelState,
  id: string,
): FilterStatusMapBookingPanel[] =>
  fromEntities.getItineraryFilterStatusMapBookingPanel(state.entities, id);
export const getItineraryErrorBookingPanel = (
  state: FlightBookingPanelState,
  id: string,
): ErrorType | undefined => fromEntities.getItineraryErrorBookingPanel(state.entities, id);

// paymentDiscounts
export const getPaymentDiscountsForItineraryBookingPanel = (
  state: FlightBookingPanelState,
  id: string,
): PaymentDiscountsBookingPanel | undefined =>
  fromEntities.getPaymentDiscountsForItineraryBookingPanel(state.entities, id);
export const getAvailablePaymentDiscountsForItineraryBookingPanel = (
  state: FlightBookingPanelState,
  id: string,
): PaymentDiscountsBookingPanel | undefined =>
  fromEntities.getAvailablePaymentDiscountsForItineraryBookingPanel(state.entities, id);
export const getFilteredPaymentDiscountsForItineraryBookingPanel = (
  state: FlightBookingPanelState,
  id: string,
): PaymentDiscountsBookingPanel | undefined =>
  fromEntities.getFilteredPaymentDiscountsForItineraryBookingPanel(state.entities, id);
export const getSelectedPaymentMethodFilter = (
  state: FlightBookingPanelState,
): string[] | undefined => fromEntities.getSelectedPaymentMethodFilter(state.entities);
export const getPaymentDiscountTooltipDismissed = (state: FlightBookingPanelState): boolean =>
  fromEntities.getPaymentDiscountTooltipDismissed(state.entities);
export const getPaymentDiscountNames = (
  state: FlightBookingPanelState,
  id: string,
  enablePaymentDiscounts: boolean,
): string[] => fromEntities.getPaymentDiscountNames(state.entities, id, enablePaymentDiscounts);

// filters
export const getBookingPanelSelectedFilters = (state: FlightBookingPanelState): AttributeType[] =>
  fromFeatures.getBookingPanelSelectedFilters(state.features);

// transitionedToFlightBookingPanel
export const getTransitionedToFlightBookingPanel = (
  state: FlightBookingPanelState,
): TransitionedToFlightBookingPanelState =>
  fromFeatures.getTransitionedToFlightBookingPanel(state.features);
